<template>
  <b-card no-body>
    <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
      <b-card-title class="mb-0">
        조건검색
      </b-card-title>
      <b-button
        variant="primary"
        :disabled="loading"
        @click="fetchList"
      >
        <b-spinner
          v-if="loading"
          small
          class="mr-50"
        />
        목록조회
      </b-button>
    </b-card-header>

    <b-card-body>
      <b-alert
        v-if="errorMessage"
        variant="danger"
        show
      >
        {{ errorMessage }}
      </b-alert>

      <b-table
        :items="items"
        :fields="fields"
        responsive
        small
        show-empty
        empty-text="조회된 조건검색식이 없습니다."
        :busy="loading"
      >
        <template #cell(actions)="row">
          <div class="d-flex">
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-50"
              :disabled="requestingSeq === row.item.seq && requestType === 'general'"
              @click="executeGeneral(row.item)"
            >
              일반요청
            </b-button>
            <b-button
              size="sm"
              variant="outline-success"
              :disabled="requestingSeq === row.item.seq && requestType === 'realtime'"
              @click="executeRealtime(row.item)"
            >
              실시간요청
            </b-button>
          </div>
        </template>
      </b-table>

      <div class="d-flex justify-content-center mt-2">
        <b-button
          v-if="hasMore"
          variant="outline-primary"
          :disabled="loadingMore"
          @click="loadMore"
        >
          <b-spinner
            v-if="loadingMore"
            small
            class="mr-50"
          />
          더 불러오기
        </b-button>
      </div>
    </b-card-body>

    <b-modal
      id="condition-search-result-modal"
      v-model="resultModalVisible"
      size="xl"
      :title="resultTitle"
      hide-footer
    >
      <b-alert
        v-if="resultErrorMessage"
        variant="danger"
        show
      >
        {{ resultErrorMessage }}
      </b-alert>

      <div
        v-if="resultLoading"
        class="text-center py-2"
      >
        <b-spinner />
      </div>

      <b-table
        v-else
        :items="resultItems"
        :fields="resultFields"
        responsive
        small
        show-empty
        empty-text="조회된 결과가 없습니다."
      >
        <template #cell(currentPrice)="row">
          {{ formatNumber(row.item.currentPrice) }}
        </template>
        <template #cell(prevDiff)="row">
          {{ formatPrevDiff(row.item.prevDiff, row.item.prevSign) }}
        </template>
        <template #cell(fluctuationRate)="row">
          <span :class="rateClass(row.item.prevSign)">{{ formatRate(row.item.fluctuationRate, row.item.prevSign) }}</span>
        </template>
        <template #cell(volume)="row">
          {{ formatNumber(row.item.volume) }}
        </template>
        <template #cell(tradeAmount)="row">
          {{ formatTradeAmount(row.item.currentPrice, row.item.volume) }}
        </template>
        <template #cell(openPrice)="row">
          {{ formatNumber(row.item.openPrice) }}
        </template>
        <template #cell(highPrice)="row">
          {{ formatNumber(row.item.highPrice) }}
        </template>
        <template #cell(lowPrice)="row">
          {{ formatNumber(row.item.lowPrice) }}
        </template>
      </b-table>

      <div class="d-flex justify-content-center mt-2">
        <b-button
          v-if="resultHasMore"
          variant="outline-primary"
          :disabled="resultLoadingMore"
          @click="loadMoreResult"
        >
          <b-spinner
            v-if="resultLoadingMore"
            small
            class="mr-50"
          />
          결과 더 불러오기
        </b-button>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import axios from '@axios'
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BModal,
    BSpinner,
    BTable,
  },
  data() {
    return {
      loading: false,
      loadingMore: false,
      items: [],
      hasMore: false,
      nextKey: '',
      errorMessage: '',
      requestingSeq: '',
      requestType: '',
      resultModalVisible: false,
      resultLoading: false,
      resultLoadingMore: false,
      resultErrorMessage: '',
      resultItems: [],
      resultHasMore: false,
      resultNextKey: '',
      resultRequestType: '',
      resultSeq: '',
      resultName: '',
      fields: [
        { key: 'seq', label: '일련번호' },
        { key: 'name', label: '조건검색식명' },
        { key: 'actions', label: '요청' },
      ],
      resultFields: [
        { key: 'code', label: '종목코드' },
        { key: 'name', label: '종목명' },
        { key: 'currentPrice', label: '현재가' },
        { key: 'prevDiff', label: '전일대비' },
        { key: 'fluctuationRate', label: '등락률' },
        { key: 'volume', label: '거래량' },
        { key: 'tradeAmount', label: '거래대금(억)' },
        { key: 'openPrice', label: '시가' },
        { key: 'highPrice', label: '고가' },
        { key: 'lowPrice', label: '저가' },
      ],
    }
  },
  computed: {
    resultTitle() {
      if (!this.resultName) return '조건검색 결과'
      return `${this.resultName} (${this.resultRequestType === 'realtime' ? '실시간요청' : '일반요청'})`
    },
  },
  methods: {
    parseApiResponse(response) {
      const payload = response && response.data ? response.data : null
      if (!payload || payload.success !== true) {
        throw new Error(payload && payload.error ? payload.error : '요청 처리 중 오류가 발생했습니다.')
      }
      return payload.data
    },
    async fetchList() {
      this.loading = true
      this.errorMessage = ''
      this.hasMore = false
      this.nextKey = ''
      try {
        const data = await this.requestList('N', '')
        this.items = Array.isArray(data.items) ? data.items : []
        this.hasMore = !!data.hasMore
        this.nextKey = data.nextKey || ''
      } catch (error) {
        this.items = []
        this.errorMessage = error && error.message ? error.message : '조건검색 목록 조회에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      if (!this.hasMore || !this.nextKey) return
      this.loadingMore = true
      this.errorMessage = ''
      try {
        const data = await this.requestList('Y', this.nextKey)
        this.items = this.items.concat(Array.isArray(data.items) ? data.items : [])
        this.hasMore = !!data.hasMore
        this.nextKey = data.nextKey || ''
      } catch (error) {
        this.errorMessage = error && error.message ? error.message : '조건검색 목록 추가 조회에 실패했습니다.'
      } finally {
        this.loadingMore = false
      }
    },
    async requestList(contYn, nextKey) {
      const response = await axios.post('/api/kiwoom/condition-search/list', { contYn, nextKey })
      return this.parseApiResponse(response)
    },
    async executeGeneral(item) {
      await this.executeRequest(item, 'general')
    },
    async executeRealtime(item) {
      await this.executeRequest(item, 'realtime')
    },
    async executeRequest(item, type) {
      if (!item || !item.seq) return
      this.requestingSeq = item.seq
      this.requestType = type
      this.resultModalVisible = true
      this.resultLoading = true
      this.resultErrorMessage = ''
      this.resultItems = []
      this.resultHasMore = false
      this.resultNextKey = ''
      this.resultRequestType = type
      this.resultSeq = item.seq
      this.resultName = item.name || ''

      try {
        const data = await this.requestResult(type, item.seq, 'N', '')
        this.resultItems = this.sortResultItems(Array.isArray(data.items) ? data.items : [])
        this.resultHasMore = !!data.hasMore
        this.resultNextKey = data.nextKey || ''
      } catch (error) {
        this.resultErrorMessage = error && error.message ? error.message : '조건검색 요청에 실패했습니다.'
      } finally {
        this.resultLoading = false
        this.requestingSeq = ''
        this.requestType = ''
      }
    },
    async loadMoreResult() {
      if (this.resultRequestType !== 'general' || !this.resultHasMore || !this.resultNextKey) return
      this.resultLoadingMore = true
      try {
        const data = await this.requestResult('general', this.resultSeq, 'Y', this.resultNextKey)
        this.resultItems = this.sortResultItems(this.resultItems.concat(Array.isArray(data.items) ? data.items : []))
        this.resultHasMore = !!data.hasMore
        this.resultNextKey = data.nextKey || ''
      } catch (error) {
        this.resultErrorMessage = error && error.message ? error.message : '조건검색 결과 추가 조회에 실패했습니다.'
      } finally {
        this.resultLoadingMore = false
      }
    },
    async requestResult(type, seq, contYn, nextKey) {
      const url = type === 'realtime' ? '/api/kiwoom/condition-search/realtime' : '/api/kiwoom/condition-search/general'
      const response = await axios.post(url, {
        seq,
        stexTp: 'K',
        contYn,
        nextKey,
      })
      return this.parseApiResponse(response)
    },
    sortResultItems(items) {
      return [...items].sort((leftItem, rightItem) => {
        const left = this.parseKiwoomNumber(leftItem && leftItem.fluctuationRate, 3)
        const right = this.parseKiwoomNumber(rightItem && rightItem.fluctuationRate, 3)
        return (right === null ? Number.NEGATIVE_INFINITY : right) - (left === null ? Number.NEGATIVE_INFINITY : left)
      })
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(String(value).replace(/,/g, ''))
      if (Number.isNaN(parsed)) return value
      return parsed.toLocaleString()
    },
    formatPrevDiff(value, prevSign) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = this.parseKiwoomNumber(value)
      if (parsed === null) return value
      return `${this.resolveSignPrefix(prevSign, parsed)}${Math.abs(parsed).toLocaleString()}`
    },
    formatRate(value, prevSign) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = this.parseKiwoomNumber(value, 3)
      if (parsed === null) return value
      return `${this.resolveSignPrefix(prevSign, parsed)}${Math.abs(parsed).toFixed(3)}%`
    },
    formatTradeAmount(currentPrice, volume) {
      const price = this.parseKiwoomNumber(currentPrice)
      const qty = this.parseKiwoomNumber(volume)
      if (price === null || qty === null) return '-'
      const amountInEok = (Math.abs(price) * Math.abs(qty)) / 100000000
      const digits = amountInEok >= 100 ? 0 : 1
      return amountInEok.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    },
    parseKiwoomNumber(value, scale = 0) {
      const text = String(value).trim()
      if (!text) return null
      const normalized = text.replace(/,/g, '')
      if (!/^[+-]?\d+$/.test(normalized)) return null
      const parsed = Number(normalized)
      if (Number.isNaN(parsed)) return null
      if (scale <= 0) return parsed
      return parsed / (10 ** scale)
    },
    resolveSignPrefix(prevSign, parsed) {
      const signCode = String(prevSign || '').trim()
      if (signCode === '4' || signCode === '5' || parsed < 0) return '-'
      if (signCode === '1' || signCode === '2' || parsed > 0) return '+'
      return ''
    },
    rateClass(prevSign) {
      const value = String(prevSign || '').trim()
      if (value === '1' || value === '2') return 'text-danger'
      if (value === '4' || value === '5') return 'text-primary'
      return 'text-body'
    },
  },
}
</script>
